import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { translations } from "./translations";
import { 
  Menu, X, ChevronDown, MapPin, Phone, Mail, Clock, 
  Instagram, Facebook, ArrowRight, Star, Check,
  Sparkles, Heart, Award, Gift, CreditCard, Send,
  Coffee, Smile, Users, Plus, Minus, HelpCircle, MessageCircle,
  Share2, Copy, MessageSquare
} from "lucide-react";

// Logo URLs
const LOGOS = {
  peach: "https://customer-assets.emergentagent.com/job_65a5757a-c277-4228-83ec-2cc31403c244/artifacts/ocmqush4_watermark.png",
  script: "https://customer-assets.emergentagent.com/job_65a5757a-c277-4228-83ec-2cc31403c244/artifacts/z9dlf3kd_Kopi%20av%20Boho-BK-Logos-Watermarks%20%285%29.png",
};

// Actual salon images from user
const SALON_IMAGES = {
  reception: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/qi0jng5m_8R5A2980..1png.png",
  counter: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/54ggmjdv_8R5A2960.jpg",
  hallway: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/fcpnzhrx_8R5A3015-2.jpg",
  treatmentBed: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/m8rbxgpv_8R5A2954-2.jpg",
  treatmentRoom: "https://customer-assets.emergentagent.com/job_6d21e2b2-3a71-4c6a-b725-c3baf5e9ff1b/artifacts/wqas3kem_8R5A3004.jpg",
  treatment: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/1uwu6huh_8R5A3004.jpg",
  // Lash lift treatment photos
  lashTreatment: "https://customer-assets.emergentagent.com/job_6d21e2b2-3a71-4c6a-b725-c3baf5e9ff1b/artifacts/qnynf1qm_Minimalist%20Simple%20Beauty%20Photo%20Collage%20Instagram%20Post%20%281%29.png",
  happyClient: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/cpexjign_8R5A3059-2.jpg",
  kamillaWorking: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/guq60kc4_8R5A3093-2.jpg",
  waterStation: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/vteta164_8R5A3022-3.jpg",
  // Detail shots
  candleDecor: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/5j6z2y63_8R5A3046.jpg",
  certificates: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/xzgi4k59_8R5A3017-2.jpg",
  waxTools: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/kqu9bi95_8R5A3029.jpg",
  waterDispenser: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/4651d9xm_8R5A3022-3.jpg",
  // Team photos
  kamillaPortrait: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/watpx1oq_561176676_25735237756079253_3220647157152149551_n.jpg",
  lyraPortrait: "https://customer-assets.emergentagent.com/job_just-peachy-salon/artifacts/yff6jq5p_8R5A3282.jpg",
};

// Service images
const SERVICE_IMAGES = {
  brows: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/aqcg3jlt_8R5A3059-2.jpg",
  waxing: "https://customer-assets.emergentagent.com/job_6d21e2b2-3a71-4c6a-b725-c3baf5e9ff1b/artifacts/gvlf08zq_Minimalist%20Simple%20Beauty%20Photo%20Collage%20Instagram%20Post%20%283%29.png",
  lashLift: "https://images.pexels.com/photos/4046564/pexels-photo-4046564.jpeg?auto=compress&cs=tinysrgb&w=800",
  courses: "https://customer-assets.emergentagent.com/job_6d21e2b2-3a71-4c6a-b725-c3baf5e9ff1b/artifacts/b3lf2kug_Minimalist%20Simple%20Beauty%20Photo%20Collage%20Instagram%20Post%20%282%29.png",
};

const BOOKING_URL = "/book";

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/justpeachy_bergen/",
  tiktok: "https://www.tiktok.com/@justpeachy.bergen",
  facebook: "https://www.facebook.com/justpeachybergen"
};

// Language Context
const LanguageContext = createContext();
export const useLanguage = () => useContext(LanguageContext);

// TikTok Icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Vipps Icon
const VippsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z"/>
  </svg>
);

// Navigation Component
const Navigation = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: t.nav.home },
    { href: '/behandlinger', label: t.nav.services },
    { href: '/om-oss', label: t.nav.about },
    { href: '/kurs', label: t.nav.courses },
    { href: '/gavekort', label: t.nav.giftcard },
    { href: '/faq', label: t.nav.faq },
    { href: '/kontakt', label: t.nav.contact },
  ];

  const navBg = scrolled || !isHome 
    ? 'bg-[#ffe2d0]/95 backdrop-blur-md border-b border-[#E6C7B8]' 
    : 'bg-transparent';
  
  const textColor = scrolled || !isHome ? 'text-[#4a3f35]' : 'text-white';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Peach Icon Only */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={LOGOS.peach} 
                alt="Just Peachy Bergen" 
                className="h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-xs uppercase tracking-[0.12em] font-medium transition-colors duration-300 luxury-underline ${textColor} hover:text-[#d4a088]`}
                  data-testid={`nav-link-${link.href.slice(1) || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Switcher */}
              <div className="flex gap-1" data-testid="language-switcher">
                <button
                  onClick={() => setLang('no')}
                  className={`text-xs font-medium tracking-wider px-2 py-1 rounded transition-all ${
                    lang === 'no' 
                      ? 'bg-[#cfa38e] text-white' 
                      : `${textColor} hover:text-[#d4a088]`
                  }`}
                  data-testid="lang-btn-no"
                >
                  NO
                </button>
                <button
                  onClick={() => setLang('en')}
                  className={`text-xs font-medium tracking-wider px-2 py-1 rounded transition-all ${
                    lang === 'en' 
                      ? 'bg-[#cfa38e] text-white' 
                      : `${textColor} hover:text-[#d4a088]`
                  }`}
                  data-testid="lang-btn-en"
                >
                  EN
                </button>
              </div>

              {/* Book Button */}
              <Link
                to={BOOKING_URL}
                className="bg-[#cfa38e] text-white px-6 py-3 text-xs uppercase tracking-[0.12em] font-semibold hover:bg-[#a68672] transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                data-testid="nav-book-btn"
              >
                {t.nav.book}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 ${textColor}`}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-[#ffe2d0] border-t border-[#E6C7B8]"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-6 space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sm uppercase tracking-wider text-[#4a3f35] hover:text-[#d4a088] py-2 border-b border-[#E6C7B8]/50"
                    data-testid={`mobile-nav-${link.href.slice(1) || 'home'}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setLang('no')}
                    className={`text-sm font-medium px-3 py-1 rounded ${lang === 'no' ? 'bg-[#cfa38e] text-white' : 'text-[#4a3f35]'}`}
                  >
                    Norsk
                  </button>
                  <button
                    onClick={() => setLang('en')}
                    className={`text-sm font-medium px-3 py-1 rounded ${lang === 'en' ? 'bg-[#cfa38e] text-white' : 'text-[#4a3f35]'}`}
                  >
                    English
                  </button>
                </div>
                <Link
                  to={BOOKING_URL}
                  className="block w-full bg-[#cfa38e] text-white text-center py-4 text-sm uppercase tracking-wider font-semibold rounded-full mt-4 shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                  data-testid="mobile-book-btn"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t.nav.book}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

// Newsletter Popup Component
const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if popup should be shown (once per week)
    const lastDismissed = localStorage.getItem('newsletterPopupDismissed');
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    
    const shouldShowPopup = () => {
      if (!lastDismissed) return true;
      const timeSinceDismissed = Date.now() - parseInt(lastDismissed, 10);
      return timeSinceDismissed > oneWeekInMs;
    };

    if (shouldShowPopup()) {
      // Show popup after 5 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Load Zoho script when popup opens
  useEffect(() => {
    if (isOpen && !submitted) {
      const script = document.createElement('script');
      script.src = 'https://zcv2-zcmp.maillist-manage.eu/js/optin.min.js';
      script.onload = () => {
        if (window.setupSF) {
          window.setupSF('sf3zd45a5060461ccaaed9751df324c824e3c06a2ea8d59f62a331e17436eb90ffd8', 'ZCFORMVIEW', false, 'light', false, '0');
        }
      };
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isOpen, submitted]);

  const handleClose = () => {
    setIsOpen(false);
    // Store the current timestamp when dismissed
    localStorage.setItem('newsletterPopupDismissed', Date.now().toString());
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-[#ffe2d0] rounded-3xl p-8 md:p-10 max-w-md w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white transition-colors z-10"
              data-testid="newsletter-close"
            >
              <X size={18} className="text-[#4a3f35]" />
            </button>

            {!submitted ? (
              <>
                {/* Icon */}
                <div className="w-16 h-16 rounded-full bg-[#cfa38e] flex items-center justify-center mx-auto mb-6">
                  <Mail size={28} className="text-white" />
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl md:text-3xl text-[#4a3f35] text-center mb-3">
                  Hold deg oppdatert!
                </h3>

                {/* Description */}
                <p className="text-[#6b5d52] text-center mb-6 leading-relaxed">
                  Få eksklusive oppdateringer om nye behandlinger, kurs og spesialtilbud rett i innboksen.<br />
                  Vi sender kun e-post når det er noe verdt å dele 🍑
                </p>

                {/* Zoho Form */}
                <form 
                  method="POST" 
                  id="zcampaignOptinForm" 
                  action="https://zcv2-zcmp.maillist-manage.eu/weboptin.zc" 
                  target="_zcSignup"
                  className="space-y-4"
                  onSubmit={() => {
                    setSubmitted(true);
                    localStorage.setItem('newsletterPopupDismissed', Date.now().toString());
                  }}
                >
                  <input
                    type="email"
                    name="CONTACT_EMAIL"
                    id="EMBED_FORM_EMAIL_LABEL"
                    placeholder="Din e-postadresse"
                    className="w-full px-5 py-4 rounded-full bg-white border border-[#E6C7B8] focus:outline-none focus:border-[#F8C8B4] text-[#4a3f35] placeholder-[#b0927f]"
                    required
                    data-testid="newsletter-email"
                  />
                  
                  {/* Hidden Zoho fields */}
                  <input type="hidden" id="fieldBorder" value="" />
                  <input type="hidden" id="submitType" name="submitType" value="optinCustomView" />
                  <input type="hidden" id="emailReportId" name="emailReportId" value="" />
                  <input type="hidden" id="formType" name="formType" value="QuickForm" />
                  <input type="hidden" name="zx" id="cmpZuid" value="14ae697efb" />
                  <input type="hidden" name="zcvers" value="2.0" />
                  <input type="hidden" name="oldListIds" id="allCheckedListIds" value="" />
                  <input type="hidden" id="mode" name="mode" value="OptinCreateView" />
                  <input type="hidden" id="zcld" name="zcld" value="13805d26b77158b9" />
                  <input type="hidden" id="zctd" name="zctd" value="13805d26b7697159" />
                  <input type="hidden" id="document_domain" value="" />
                  <input type="hidden" id="zc_Url" value="zcv2-zcmp.maillist-manage.eu" />
                  <input type="hidden" id="new_optin_response_in" value="0" />
                  <input type="hidden" id="duplicate_optin_response_in" value="0" />
                  <input type="hidden" name="zc_trackCode" id="zc_trackCode" value="ZCFORMVIEW" />
                  <input type="hidden" id="zc_formIx" name="zc_formIx" value="3zd45a5060461ccaaed9751df324c824e3c06a2ea8d59f62a331e17436eb90ffd8" />
                  <input type="hidden" id="viewFrom" value="URL_ACTION" />
                  
                  <button
                    type="submit"
                    name="SIGNUP_SUBMIT_BUTTON"
                    id="zcWebOptin"
                    className="w-full bg-[#cfa38e] hover:bg-[#d4a088] text-white py-4 rounded-full font-semibold uppercase tracking-wider text-sm transition-colors shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                    data-testid="newsletter-submit"
                  >
                    Ja, jeg vil være med!
                  </button>
                </form>

                {/* No spam note */}
                <p className="text-xs text-[#b0927f] text-center mt-4">
                  Ingen spam, kun det beste fra Just Peachy Bergen
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#cfa38e] flex items-center justify-center mx-auto mb-6">
                  <Check size={28} className="text-white" />
                </div>
                <h3 className="font-serif text-2xl text-[#4a3f35] mb-2">Tusen takk!</h3>
                <p className="text-[#6b5d52]">Vi gleder oss til å holde deg oppdatert 🍑</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background Image - Salon Reception */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${SALON_IMAGES.reception})` }}
      />
      
      {/* Darker Overlay for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#333333]/70 via-[#333333]/50 to-[#333333]/70" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center text-white">
        {/* Text backdrop for better readability */}
        <div className="bg-[#a68672]/40 backdrop-blur-sm rounded-3xl p-8 md:p-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-sm md:text-base uppercase tracking-[0.25em] text-[#FCE8E0] mb-4"
            data-testid="hero-tagline"
          >
            {t.hero.tagline}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-4xl md:text-6xl lg:text-7xl mb-4 leading-tight font-medium"
            data-testid="hero-title"
          >
            {t.hero.title}<br />
            <span className="text-[#d4a088] italic">{t.hero.titleHighlight}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}<br />
            {t.hero.subtitleLine2}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to={BOOKING_URL}
              className="inline-flex items-center justify-center gap-3 bg-[#cfa38e] hover:bg-white hover:text-[#4a3f35] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
              data-testid="hero-cta-btn"
            >
              {t.hero.cta}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/gavekort"
              className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white/80 hover:bg-white hover:text-[#4a3f35] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full"
              data-testid="hero-giftcard-btn"
            >
              <Gift size={18} />
              {translations.no.nav.giftcard}
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator - Outside content container */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
      >
        <span className="text-xs uppercase tracking-widest text-white/60">{t.hero.scrollText}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
          <ChevronDown size={24} className="text-[#d4a088]" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = ({ fullPage = false }) => {
  const { t } = useLanguage();

  const stats = [
    { number: '10+', label: t.about.experience, icon: <Award size={24} /> },
    { number: '1500+', label: t.about.clients, icon: <Smile size={24} /> },
    { number: '100000+', label: t.about.treatments, icon: <Heart size={24} /> }
  ];

  return (
    <section id="about" className={`${fullPage ? 'pt-32' : ''} py-20 md:py-28 bg-[#ffe2d0]`} data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Images Grid */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="space-y-4">
              <div className="aspect-[4/5] overflow-hidden rounded-2xl img-zoom">
                <img src={SALON_IMAGES.hallway} alt="Just Peachy Bergen interior" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square overflow-hidden rounded-2xl img-zoom">
                <img src={SALON_IMAGES.counter} alt="Italwax products" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="pt-8 space-y-4">
              <div className="aspect-square overflow-hidden rounded-2xl img-zoom">
                <img src={SALON_IMAGES.treatmentBed} alt="Treatment room" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[4/5] overflow-hidden rounded-2xl img-zoom">
                <img src={SALON_IMAGES.treatmentRoom} alt="Treatment room with cozy blanket" className="w-full h-full object-cover" />
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold" data-testid="about-tagline">
              {t.about.tagline}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] mb-6 leading-tight font-medium" data-testid="about-title">
              {t.about.title}<br />
              <span className="text-[#d4a088] italic">{t.about.titleHighlight}</span>
            </h2>
            <p className="text-[#6b5d52] leading-relaxed mb-4" data-testid="about-description">
              {t.about.description}
            </p>
            <p className="text-[#6b5d52] leading-relaxed mb-4">
              {t.about.description2}
            </p>
            <p className="text-[#6b5d52] leading-relaxed mb-8">
              {t.about.description3}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#E6C7B8]">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-[#d4a088] flex justify-center mb-2">{stat.icon}</div>
                  <p className="font-serif text-2xl md:text-3xl text-[#4a3f35] font-medium">{stat.number}</p>
                  <p className="text-xs uppercase tracking-wider text-[#6b5d52] mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            {!fullPage && (
              <Link
                to={BOOKING_URL}
                className="inline-flex items-center gap-3 bg-[#cfa38e] hover:bg-[#a68672] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)] mt-8"
                data-testid="about-cta"
              >
                {t.about.cta}
                <ArrowRight size={18} />
              </Link>
            )}
          </motion.div>
        </div>

        {/* Team Section - Only on full page */}
        {fullPage && t.about.team && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-20 md:mt-28"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-[#4a3f35] text-center mb-12 font-medium">
              {t.about.team.title}
            </h2>

            {/* Owner - Kamilla (Featured) */}
            <div className="max-w-3xl mx-auto mb-16">
              <div className="bg-white rounded-2xl p-8 shadow-soft">
                <div className="flex flex-col items-center text-center">
                  <div className="flex-shrink-0 mb-6">
                    <div className="relative">
                      <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-3 border-[#F8C8B4]">
                        <img 
                          src={SALON_IMAGES.kamillaPortrait} 
                          alt={t.about.team.owner.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#cfa38e] text-white text-[10px] uppercase tracking-wider px-3 py-1 rounded-full font-semibold whitespace-nowrap">
                        {t.about.team.owner.role}
                      </div>
                    </div>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-2xl md:text-3xl text-[#4a3f35] mb-4 font-medium">
                      {t.about.team.owner.name}
                    </h3>
                    <div className="text-sm text-[#6b5d52] leading-relaxed space-y-3 text-center max-w-2xl mx-auto">
                      <p>{t.about.team.owner.description}</p>
                      {t.about.team.owner.description2 && <p>{t.about.team.owner.description2}</p>}
                      {t.about.team.owner.description3 && <p>{t.about.team.owner.description3}</p>}
                      {t.about.team.owner.description4 && <p>{t.about.team.owner.description4}</p>}
                      {t.about.team.owner.description5 && <p className="italic">{t.about.team.owner.description5}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Staff */}
            <div className="mb-12">
              <h3 className="text-xs uppercase tracking-[0.25em] text-[#d4a088] text-center mb-8 font-semibold">
                {t.about.team.staffTitle}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                {t.about.team.staff.map((member, index) => {
                  const staffImage = member.name === 'Lyra'
                    ? SALON_IMAGES.lyraPortrait
                    : (member.image || 'https://customer-assets-agu9un31.emergentagent.net/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/3awqqyj0_Design%20uten%20navn%20%283%29.png');
                  return (
                    <div key={index} className="bg-white rounded-2xl p-8 shadow-soft">
                      <div className="flex flex-col items-center text-center">
                        <div className="flex-shrink-0 mb-6">
                          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-3 border-[#F8C8B4]">
                            <img
                              src={staffImage}
                              alt={member.name}
                              className="w-full h-full object-cover"
                              style={{ objectPosition: '50% 20%' }}
                            />
                          </div>
                        </div>
                        <div className="w-full">
                          <h4 className="font-serif text-2xl text-[#4a3f35] mb-1 font-medium">{member.name}</h4>
                          <p className="text-xs uppercase tracking-wider text-[#d4a088] mb-4">{member.role}</p>
                          <p className="text-sm text-[#6b5d52] mb-3 leading-relaxed">{member.description}</p>
                          {member.description2 && (
                            <p className="text-sm text-[#6b5d52] mb-3 leading-relaxed">{member.description2}</p>
                          )}
                          {member.description3 && (
                            <p className="text-sm text-[#6b5d52] leading-relaxed">{member.description3}</p>
                          )}
                          {member.funfact && (
                            <p className="text-xs text-[#d4a088] mt-4 italic">{member.funfact}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trainees */}
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] text-[#d4a088] text-center mb-8 font-semibold">
                {t.about.team.traineesTitle}
              </h3>
              <div className="flex justify-center gap-8 flex-wrap">
                {t.about.team.trainees.map((trainee, index) => (
                  <div key={index} className="bg-[#FCE8E0] rounded-2xl p-6 text-center max-w-[320px]">
                    <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4 border-3 border-[#E6C7B8]">
                      {trainee.image ? (
                        <img src={trainee.image} alt={trainee.name} className="w-full h-full object-cover" style={{ objectPosition: '50% 20%' }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white">
                          <Sparkles size={32} className="text-[#d4a088]" />
                        </div>
                      )}
                    </div>
                    <h4 className="font-serif text-xl text-[#4a3f35] mb-1 font-medium">{trainee.name}</h4>
                    <p className="text-xs uppercase tracking-wider text-[#d4a088] mb-3">{trainee.role}</p>
                    <p className="text-sm text-[#6b5d52] leading-relaxed">{trainee.description}</p>
                    {trainee.funfact && (
                      <p className="text-xs text-[#d4a088] mt-3 italic">{trainee.funfact}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <Link
                to={BOOKING_URL}
                className="inline-flex items-center gap-3 bg-[#cfa38e] hover:bg-[#a68672] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
                data-testid="about-cta"
              >
                {t.about.cta}
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Video Section */}
            {t.about.video && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="mt-20 md:mt-28"
              >
                <div className="text-center mb-10">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold">
                    {t.about.video.tagline}
                  </p>
                  <h2 className="font-serif text-3xl md:text-4xl text-[#4a3f35] mb-4 font-medium">
                    {t.about.video.title} <span className="text-[#d4a088] italic">{t.about.video.titleHighlight}</span>
                  </h2>
                  <p className="text-[#6b5d52] max-w-2xl mx-auto leading-relaxed">
                    {t.about.video.description}
                  </p>
                </div>
                
                <div className="max-w-xs mx-auto">
                  <div className="rounded-2xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.1)]">
                    <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src="https://www.youtube.com/embed/M0ZKfu01pfc"
                        title="Just Peachy Bergen - Intervju"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
        
        {/* Photo Credit */}
        {fullPage && (
          <p className="text-center text-xs text-[#b0927f] mt-12">Foto: APEX BERGEN</p>
        )}
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = ({ fullPage = false }) => {
  const { t } = useLanguage();

  const services = [
    { key: 'brows', image: SERVICE_IMAGES.brows, icon: <Sparkles size={20} />, link: null, ...t.services.brows },
    { key: 'waxing', image: SERVICE_IMAGES.waxing, icon: <Heart size={20} />, link: null, ...t.services.waxing },
    { key: 'lashLift', image: SALON_IMAGES.lashTreatment, icon: <Star size={20} />, link: '/vippeloft', ...t.services.lashLift },
    { key: 'courses', image: SERVICE_IMAGES.courses, icon: <Award size={20} />, link: '/kurs', ...t.services.facial }
  ];

  return (
    <section id="services" className={`${fullPage ? 'pt-32' : ''} py-20 md:py-28 bg-[#FCE8E0]`} data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold" data-testid="services-tagline">
            {t.services.tagline}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] leading-tight font-medium" data-testid="services-title">
            {t.services.title} <span className="text-[#d4a088] italic">{t.services.titleHighlight}</span>
          </h2>
          <p className="text-[#6b5d52] mt-4 max-w-2xl mx-auto">{t.services.subtitle}</p>
          
          {/* Student Discount Banner - Only on homepage */}
          {!fullPage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-soft"
            >
              <Award size={16} className="text-[#cfa38e]" />
              <span className="text-sm text-[#4a3f35]">Psst... Vi har <span className="font-semibold text-[#cfa38e]">10% studentrabatt!</span></span>
            </motion.div>
          )}
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-soft card-hover"
              data-testid={`service-card-${service.key}`}
            >
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#d4a088] mb-2">
                  {service.icon}
                  <span className="text-xs font-semibold uppercase tracking-wider">{service.price}</span>
                </div>
                <h3 className="font-serif text-xl text-[#4a3f35] mb-2 font-medium">{service.title}</h3>
                <p className="text-sm text-[#6b5d52] leading-relaxed mb-4">{service.description}</p>
                {service.link ? (
                  <Link
                    to={service.link}
                    className="inline-flex items-center gap-2 text-[#d4a088] text-sm font-semibold hover:text-[#4a3f35] transition-colors"
                    data-testid={`service-cta-${service.key}`}
                  >
                    {service.cta}
                    <ArrowRight size={14} />
                  </Link>
                ) : (
                  <Link
                    to={BOOKING_URL}
                    className="inline-flex items-center gap-2 text-[#d4a088] text-sm font-semibold hover:text-[#4a3f35] transition-colors"
                    data-testid={`service-cta-${service.key}`}
                  >
                    {service.cta}
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Prices & Terms Section - Only show on full page */}
        {fullPage && t.services.pricesInfo && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-20 md:mt-28"
          >
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold">
                {t.services.pricesInfo.tagline}
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#4a3f35] mb-4 font-medium">
                {t.services.pricesInfo.title} <span className="text-[#d4a088] italic">{t.services.pricesInfo.titleHighlight}</span>
              </h2>
              <p className="text-[#6b5d52] max-w-2xl mx-auto leading-relaxed">
                {t.services.pricesInfo.intro}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Student Discount */}
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#cfa38e] flex items-center justify-center">
                    <Award size={20} className="text-white" />
                  </div>
                  <h3 className="font-serif text-lg text-[#4a3f35] font-medium">{t.services.pricesInfo.studentTitle}</h3>
                </div>
                <p className="text-[#4a3f35] font-medium mb-2">{t.services.pricesInfo.studentDesc}</p>
                <p className="text-sm text-[#6b5d52]">{t.services.pricesInfo.studentNote}</p>
              </div>

              {/* Time Surcharges */}
              <div className="bg-white rounded-2xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#cfa38e] flex items-center justify-center">
                    <Clock size={20} className="text-white" />
                  </div>
                  <h3 className="font-serif text-lg text-[#4a3f35] font-medium">{t.services.pricesInfo.surchargeTitle}</h3>
                </div>
                <p className="text-sm text-[#6b5d52] mb-3">{t.services.pricesInfo.surchargeDesc}</p>
                <ul className="space-y-2 mb-3">
                  {t.services.pricesInfo.surcharges.map((item, index) => (
                    <li key={index} className="text-[#4a3f35] text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d4a088]"></span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#6b5d52] italic">{t.services.pricesInfo.surchargeNote}</p>
              </div>

              {/* Cancellation Policy - Full width */}
              <div className="bg-white rounded-2xl p-6 shadow-soft md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#cfa38e] flex items-center justify-center">
                    <CreditCard size={20} className="text-white" />
                  </div>
                  <h3 className="font-serif text-lg text-[#4a3f35] font-medium">{t.services.pricesInfo.cancellationTitle}</h3>
                </div>
                <p className="text-sm text-[#6b5d52] mb-3">{t.services.pricesInfo.cancellationDesc}</p>
                <ul className="space-y-2">
                  {t.services.pricesInfo.cancellationRules.map((rule, index) => (
                    <li key={index} className="text-[#4a3f35] text-sm flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d4a088] mt-1.5 flex-shrink-0"></span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link
            to={BOOKING_URL}
            className="inline-flex items-center gap-3 bg-[#a68672] hover:bg-[#cfa38e] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
            data-testid="services-main-cta"
          >
            {t.services.mainCta}
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Courses Section
const CoursesSection = ({ fullPage = false }) => {
  const { t } = useLanguage();

  return (
    <section id="courses" className={`${fullPage ? 'pt-32' : ''} py-20 md:py-28 bg-[#a68672]`} data-testid="courses-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#ffd8c2] mb-3 font-semibold" data-testid="courses-tagline">
              {t.courses.tagline}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-6 leading-tight font-medium" data-testid="courses-title">
              {t.courses.title}<br />
              <span className="text-[#ffd8c2] italic">{t.courses.titleHighlight}</span>
            </h2>
            <p className="text-white/90 leading-relaxed mb-4" data-testid="courses-description">
              {t.courses.description}
            </p>
            <p className="text-white/90 leading-relaxed mb-8">
              {t.courses.description2}
            </p>

            {/* Features */}
            <ul className="space-y-3 mb-8">
              {t.courses.features.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-white/95"
                >
                  <Check size={18} className="text-[#ffd8c2] mt-1 flex-shrink-0" />
                  <span>{feature}</span>
                </motion.li>
              ))}
            </ul>

            <p className="text-[#ffd8c2] text-sm mb-6 italic">{t.courses.interested}</p>

            <a
              href="mailto:info@justpeachybergen.no?subject=Kursforespørsel"
              className="inline-flex items-center gap-3 bg-[#cfa38e] hover:bg-white hover:text-[#4a3f35] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
              data-testid="courses-cta"
            >
              <Send size={18} />
              {t.courses.cta}
            </a>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden rounded-2xl img-zoom">
              <img src={SALON_IMAGES.treatment} alt="Waxing course" className="w-full h-full object-cover" />
            </div>
            {/* Badge */}
            <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 bg-[#cfa38e] text-white p-5 md:p-6 rounded-2xl text-center shadow-warm">
              <p className="font-serif text-xl md:text-2xl font-medium">Italwax</p>
              <p className="text-xs uppercase tracking-wider mt-1 opacity-90">Certified</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Gift Card Section/Page
const GiftCardSection = ({ fullPage = false }) => {
  const { t } = useLanguage();

  const steps = [
    { icon: <Gift size={28} />, title: t.giftcard.step1Title, desc: t.giftcard.step1Desc },
    { icon: <CreditCard size={28} />, title: t.giftcard.step2Title, desc: t.giftcard.step2Desc },
    { icon: <Send size={28} />, title: t.giftcard.step3Title, desc: t.giftcard.step3Desc },
  ];

  return (
    <section id="giftcard" className={`${fullPage ? 'pt-32' : ''} py-20 md:py-28 bg-gradient-to-b from-[#ffe2d0] to-[#FCE8E0]`} data-testid="giftcard-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold" data-testid="giftcard-tagline">
            {t.giftcard.tagline}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] leading-tight font-medium" data-testid="giftcard-title">
            {t.giftcard.title} <span className="text-[#d4a088] italic">{t.giftcard.titleHighlight}</span>
          </h2>
          <p className="text-[#6b5d52] mt-4 max-w-2xl mx-auto">{t.giftcard.description}</p>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-14"
        >
          <h3 className="text-center font-serif text-2xl text-[#4a3f35] mb-8 font-medium">{t.giftcard.howItWorks}</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-soft card-hover">
                <div className="w-14 h-14 bg-[#FCE8E0] rounded-full flex items-center justify-center mx-auto mb-4 text-[#d4a088]">
                  {step.icon}
                </div>
                <div className="w-8 h-8 bg-[#cfa38e] rounded-full flex items-center justify-center mx-auto -mt-10 mb-4 text-white text-sm font-bold">
                  {index + 1}
                </div>
                <h4 className="font-serif text-lg text-[#4a3f35] mb-2 font-medium">{step.title}</h4>
                <p className="text-sm text-[#6b5d52]">{step.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Vipps Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-warm max-w-2xl mx-auto text-center"
        >
          <div className="w-20 h-20 bg-[#FF5B24] rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-white font-bold text-2xl">Vipps</span>
          </div>
          <h3 className="font-serif text-2xl text-[#4a3f35] mb-4 font-medium">{t.giftcard.vippsTitle}</h3>
          
          <div className="bg-[#ffe2d0] rounded-xl p-6 mb-6 space-y-2">
            <p className="text-[#4a3f35] font-medium">{t.giftcard.vippsSearch}</p>
            <p className="text-2xl font-bold text-[#d4a088]">{t.giftcard.vippsCode}</p>
          </div>

          {/* Amount Options */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {t.giftcard.amounts.map((amount, index) => (
              <span key={index} className="px-4 py-2 bg-[#FCE8E0] text-[#4a3f35] rounded-full text-sm font-medium">
                {amount}
              </span>
            ))}
          </div>

          <p className="text-sm text-[#6b5d52] mb-6 italic">{t.giftcard.vippsNote}</p>

          {/* Post-purchase email notice */}
          <div
            className="text-left bg-[#fff2ea] border border-[#e8c8b4] rounded-2xl p-5 mb-6 flex gap-3"
            data-testid="giftcard-vipps-email-notice"
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#d4a088] flex items-center justify-center mt-0.5">
              <Mail className="text-white" size={16} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#4a3f35] mb-1 uppercase tracking-wide">
                {t.giftcard.vippsEmailNoticeTitle}
              </p>
              <p className="text-sm text-[#6b5d52] leading-relaxed">
                {t.giftcard.vippsEmailNotice.split('info@justpeachybergen.no').map((part, i, arr) =>
                  i < arr.length - 1 ? (
                    <span key={i}>
                      {part}
                      <a
                        href="mailto:info@justpeachybergen.no"
                        className="text-[#d4a088] hover:text-[#a68672] font-semibold underline decoration-[#d4a088]/40 hover:decoration-[#a68672] transition-colors"
                      >
                        info@justpeachybergen.no
                      </a>
                    </span>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
              </p>
            </div>
          </div>

          <a
            href="https://vipps.no"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-vipps inline-flex items-center gap-2"
            data-testid="giftcard-vipps-btn"
          >
            {t.giftcard.cta}
            <ArrowRight size={18} />
          </a>

          <p className="text-sm text-[#6b5d52] mt-6">{t.giftcard.contact}</p>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-20 md:py-28 bg-[#ffe2d0]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold" data-testid="testimonials-tagline">
            {t.testimonials.tagline}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] leading-tight font-medium" data-testid="testimonials-title">
            {t.testimonials.title} <span className="text-[#d4a088] italic">{t.testimonials.titleHighlight}</span>
          </h2>
        </motion.div>

        {/* Reviews */}
        <div className="grid md:grid-cols-3 gap-6">
          {t.testimonials.reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-soft card-hover relative"
              data-testid={`testimonial-card-${index}`}
            >
              {/* Quote */}
              <span className="absolute top-4 left-6 font-serif text-5xl text-[#d4a088]/30 leading-none">"</span>
              
              {/* Stars */}
              <div className="flex gap-1 mb-4 pt-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#F8C8B4] text-[#d4a088]" />
                ))}
              </div>
              
              <p className="text-[#4a3f35] leading-relaxed mb-6">{review.text}</p>
              
              <div className="border-t border-[#E6C7B8] pt-4">
                <p className="font-medium text-[#4a3f35]">{review.name}</p>
                <p className="text-xs text-[#d4a088] uppercase tracking-wider">{review.service}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Before/After Gallery Section
const BeforeAfterSection = () => {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(0);
  
  // Before/After images for each treatment
  const beforeAfterImages = [
    // Koreansk Vippeløft
    [
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/wuq1j9tc_Gray%20Minimalist%20Skincare%20Before%20and%20After%20Instagram%20Story%20%28Instagram-innlegg%20%2845%29%29%20%281%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/qzz6hqt0_Gray%20Minimalist%20Skincare%20Before%20and%20After%20Instagram%20Story%20%28Instagram-innlegg%20%2845%29%29.png",
      },
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/uyxc4gkm_Pink%20Minimalist%20Modern%20Beauty%20Lash%20Before%20After%20Collage%20Instagram%20Post%20%281%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/5o0vqm0t_Pink%20Minimalist%20Modern%20Beauty%20Lash%20Before%20After%20Collage%20Instagram%20Post.png",
      },
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/553anagc_Gray%20Minimalist%20Skincare%20Before%20and%20After%20Instagram%20Story%20%28Instagram-innlegg%20%2845%29%29%20%283%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/s2smxog1_Gray%20Minimalist%20Skincare%20Before%20and%20After%20Instagram%20Story%20%28Instagram-innlegg%20%2845%29%29%20%282%29.png",
      }
    ],
    // HD Bryn
    [
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/0t80ofcq_selfie%20%281%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/pszes5so_selfie%20%282%29.jpg",
      }
    ],
    // Hybrid Bryn
    [
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/k7sgfxk5_selfie%20%2813%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/ylqwhmw1_selfie%20%2814%29.png",
      },
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/owfsglv6_selfie%20%2815%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/5gh1636t_selfie%20%2816%29.png",
      }
    ],
    // Brynslaminering
    [
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/wfd537k8_selfie%20%283%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/a84jeipw_selfie%20%284%29.png",
      }
    ],
    // Voksing
    [
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/9c8hsfpd_selfie%20%285%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/xm87yivw_selfie%20%286%29.png",
      },
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/hpr4jcbe_selfie%20%288%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/8a3335ql_selfie%20%287%29.png",
      },
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/s633ju9b_selfie%20%2810%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/a7xfrjgi_selfie%20%289%29.png",
      },
      {
        before: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/hd0a8iwo_selfie%20%2812%29.png",
        after: "https://customer-assets.emergentagent.com/job_55b629b9-a9b7-49a4-9b30-4a0fa5a69d68/artifacts/fl94ux6k_selfie%20%2811%29.png",
      }
    ]
  ];

  return (
    <section className="py-20 md:py-28 bg-[#a68672]" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#ffd8c2] mb-3 font-semibold">
            {t.beforeAfter.tagline}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight font-medium">
            {t.beforeAfter.title} <span className="text-[#ffd8c2] italic">{t.beforeAfter.titleHighlight}</span>
          </h2>
          <p className="text-white/80 mt-4 max-w-2xl mx-auto">{t.beforeAfter.subtitle}</p>
        </motion.div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {t.beforeAfter.items.map((item, index) => (
            <button
              key={index}
              onClick={() => setActiveCategory(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === index 
                  ? 'bg-white text-[#4a3f35]' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              data-testid={`before-after-tab-${index}`}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Before/After Grid - Side by Side */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {beforeAfterImages[activeCategory].map((pair, index) => (
            <div key={index} className="bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
              <div className="flex gap-3">
                {/* Before */}
                <div className="flex-1 relative">
                  <div className="aspect-[3/4] overflow-hidden rounded-xl">
                    <img 
                      src={pair.before} 
                      alt="Før"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 20%' }}
                    />
                  </div>
                  <span className="absolute top-3 left-3 bg-white text-[#4a3f35] text-xs uppercase tracking-wider px-3 py-1.5 rounded-full font-semibold">
                    Før
                  </span>
                </div>
                {/* After */}
                <div className="flex-1 relative">
                  <div className="aspect-[3/4] overflow-hidden rounded-xl">
                    <img 
                      src={pair.after} 
                      alt="Etter"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 20%' }}
                    />
                  </div>
                  <span className="absolute top-3 right-3 bg-[#cfa38e] text-white text-xs uppercase tracking-wider px-3 py-1.5 rounded-full font-semibold">
                    Etter
                  </span>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Category Title & Description */}
        <div className="text-center mt-8">
          <h3 className="font-serif text-xl text-white font-medium mb-2">
            {t.beforeAfter.items[activeCategory].title}
          </h3>
          <p className="text-white/70 text-sm">
            {t.beforeAfter.items[activeCategory].description}
          </p>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-10"
        >
          <Link
            to={BOOKING_URL}
            className="inline-flex items-center gap-3 bg-[#cfa38e] hover:bg-white hover:text-[#4a3f35] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
            data-testid="before-after-cta"
          >
            {t.beforeAfter.cta}
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Instagram Section — live feed from backend
const InstagramSection = () => {
  const { t } = useLanguage();
  const [posts, setPosts] = useState(null); // null = loading, [] = empty, [...] = loaded

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/instagram/feed`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPosts(Array.isArray(data) ? data.slice(0, 8) : []))
      .catch(() => setPosts([]));
  }, []);

  // Fallback to local images while loading or if backend empty
  const fallbackImages = [
    SALON_IMAGES.candleDecor,
    SALON_IMAGES.certificates,
    SALON_IMAGES.happyClient,
    SALON_IMAGES.waxTools,
    SALON_IMAGES.reception,
    SALON_IMAGES.hallway,
    SALON_IMAGES.kamillaWorking,
    SALON_IMAGES.waterDispenser
  ];

  const tiles = posts && posts.length > 0
    ? posts.map((p) => ({ src: p.image_url, link: p.permalink, caption: p.caption }))
    : fallbackImages.map((src) => ({ src, link: SOCIAL_LINKS.instagram, caption: null }));

  return (
    <section className="py-20 md:py-28 bg-[#FCE8E0]" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold" data-testid="instagram-tagline">
            {t.instagram.tagline}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] leading-tight font-medium" data-testid="instagram-title">
            {t.instagram.title}
          </h2>
          <p className="text-[#d4a088] mt-2 font-medium">{t.instagram.handle}</p>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {tiles.map((tile, index) => (
            <motion.a
              key={index}
              href={tile.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              className="relative aspect-square overflow-hidden rounded-2xl group img-zoom"
              data-testid={`instagram-img-${index}`}
            >
              <img
                src={tile.src}
                alt={tile.caption || `Just Peachy Bergen Instagram ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#cfa38e]/0 group-hover:bg-[#cfa38e]/40 transition-colors duration-300 flex items-end justify-center rounded-2xl p-3">
                <Instagram className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" size={32} />
                {tile.caption && (
                  <span className="text-white text-xs leading-snug opacity-0 group-hover:opacity-100 transition-opacity duration-300 line-clamp-2 text-center">
                    {tile.caption}
                  </span>
                )}
              </div>
            </motion.a>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-10"
        >
          <a
            href={SOCIAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#a68672] hover:bg-[#cfa38e] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
            data-testid="instagram-follow-btn"
          >
            <Instagram size={18} />
            {t.instagram.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ fullPage = false }) => {
  const { t } = useLanguage();

  return (
    <section id="contact" className={`${fullPage ? 'pt-32' : ''} py-20 md:py-28 bg-[#ffe2d0]`} data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold" data-testid="contact-tagline">
              {t.contact.tagline}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] mb-6 leading-tight font-medium" data-testid="contact-title">
              {t.contact.title}<br />
              <span className="text-[#d4a088] italic">{t.contact.titleHighlight}</span>
            </h2>
            <p className="text-[#6b5d52] mb-8">{t.contact.description}</p>

            <div className="space-y-5">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FCE8E0] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#d4a088]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6b5d52] mb-1">{t.contact.address}</p>
                  <p className="text-[#4a3f35] font-medium">Veiten 2B, 5012 Bergen</p>
                  <p className="text-sm text-[#6b5d52] mt-1">{t.contact.directions}</p>
                  <p className="text-sm text-[#6b5d52]">{t.contact.parking}</p>
                  <p className="text-sm text-[#6b5d52]">{t.contact.bus}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FCE8E0] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-[#d4a088]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6b5d52] mb-1">{t.contact.phone}</p>
                  <a href="tel:97304040" className="text-[#4a3f35] font-medium hover:text-[#d4a088] transition-colors">
                    +47 973 04 040
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FCE8E0] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#d4a088]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6b5d52] mb-1">{t.contact.email}</p>
                  <a href="mailto:info@justpeachybergen.no" className="text-[#4a3f35] font-medium hover:text-[#d4a088] transition-colors">
                    info@justpeachybergen.no
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FCE8E0] rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-[#d4a088]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6b5d52] mb-1">{t.contact.hours}</p>
                  <p className="text-[#4a3f35] font-medium">{t.contact.hoursWeekday}</p>
                  <p className="text-[#4a3f35] font-medium">{t.contact.hoursWeekend}</p>
                  <p className="text-sm text-[#6b5d52] mt-1 italic">{t.contact.hoursNote}</p>
                </div>
              </div>
            </div>

            {/* Book CTA */}
            <Link
              to={BOOKING_URL}
              className="inline-flex items-center gap-3 bg-[#cfa38e] hover:bg-[#a68672] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)] mt-8"
              data-testid="contact-book-btn"
            >
              {t.nav.book}
              <ArrowRight size={18} />
            </Link>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="aspect-square lg:aspect-auto lg:h-full min-h-[400px] bg-[#FCE8E0] overflow-hidden rounded-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1972.7089035689285!2d5.3237!3d60.3925!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x463cfc0d2a4a8d65%3A0x4a9f3a0e8c4a8c4a!2sVeiten%202B%2C%205012%20Bergen!5e0!3m2!1sen!2sno!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Just Peachy Bergen Location"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// VIP Inner Circle Section
const VIPSection = () => {
  const { t } = useLanguage();
  const vip = t.vip;

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-[#ffe2d0] to-[#FCE8E0]" data-testid="vip-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold">
            {vip.tagline}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#4a3f35] leading-tight font-medium mb-4">
            {vip.title} <span className="text-[#d4a088] italic">{vip.titleHighlight}</span>
          </h2>
          <p className="text-[#6b5d52] max-w-2xl mx-auto">{vip.subtitle}</p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-warm"
        >
          {/* Description */}
          <p className="text-center text-[#4a3f35] mb-10 max-w-2xl mx-auto leading-relaxed">
            {vip.description}
          </p>

          {/* How It Works */}
          <h3 className="font-serif text-xl text-[#4a3f35] text-center mb-8 font-medium">{vip.howItWorks}</h3>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {vip.steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-[#FCE8E0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-serif text-xl text-[#d4a088] font-medium">{index + 1}</span>
                </div>
                <h4 className="font-medium text-[#4a3f35] mb-2">{step.title}</h4>
                <p className="text-sm text-[#6b5d52]">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Benefits */}
          <div className="bg-[#FCE8E0] rounded-2xl p-6 md:p-8 mb-8">
            <h3 className="font-serif text-lg text-[#4a3f35] text-center mb-6 font-medium">{vip.benefitsTitle}</h3>
            <div className="flex flex-col items-center gap-4">
              {/* First two benefits side by side */}
              <div className="grid md:grid-cols-2 gap-4 w-full">
                {vip.benefits.slice(0, 2).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#cfa38e] rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles size={12} className="text-white" />
                    </div>
                    <span className="text-sm text-[#4a3f35]">{benefit}</span>
                  </div>
                ))}
              </div>
              {/* Third benefit centered */}
              {vip.benefits[2] && (
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#cfa38e] rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles size={12} className="text-white" />
                  </div>
                  <span className="text-sm text-[#4a3f35]">{vip.benefits[2]}</span>
                </div>
              )}
            </div>
          </div>

          {/* Note */}
          <p className="text-center text-sm text-[#6b5d52] italic mb-8 max-w-xl mx-auto">
            {vip.note}
          </p>

          {/* CTA */}
          <div className="text-center">
            <Link
              to={BOOKING_URL}
              className="inline-flex items-center gap-3 bg-[#cfa38e] hover:bg-[#a68672] text-white px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
              data-testid="vip-cta"
            >
              {vip.cta}
              <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { href: '/', label: t.nav.home },
    { href: '/behandlinger', label: t.nav.services },
    { href: '/om-oss', label: t.nav.about },
    { href: '/kurs', label: t.nav.courses },
    { href: '/gavekort', label: t.nav.giftcard },
    { href: '/kontakt', label: t.nav.contact },
  ];

  return (
    <footer className="bg-[#a68672] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        {/* Centered Large Logo */}
        <div className="text-center mb-12">
          <img src={LOGOS.script} alt="Just Peachy Bergen" className="h-32 md:h-40 mx-auto brightness-0 invert" />
          <p className="text-white/70 leading-relaxed max-w-lg mx-auto mt-6">{t.footer.description}</p>
          {/* Social */}
          <div className="flex gap-3 justify-center mt-6">
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#cfa38e] transition-all"
              data-testid="footer-instagram"
            >
              <Instagram size={18} />
            </a>
            <a 
              href={SOCIAL_LINKS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#cfa38e] transition-all"
              data-testid="footer-tiktok"
            >
              <TikTokIcon className="w-4 h-4" />
            </a>
            <a 
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#cfa38e] transition-all"
              data-testid="footer-facebook"
            >
              <Facebook size={18} />
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-8 border-t border-white/10 pt-10">
          {/* Quick Links */}
          <div className="text-center md:text-left">
            <h4 className="text-xs uppercase tracking-wider text-[#d4a088] mb-5 font-semibold">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-white/70 hover:text-[#d4a088] transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center md:text-right">
            <h4 className="text-xs uppercase tracking-wider text-[#d4a088] mb-5 font-semibold">{t.footer.contact}</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li>Veiten 2B, 5012 Bergen</li>
              <li><a href="tel:97304040" className="hover:text-[#d4a088] transition-colors">+47 973 04 040</a></li>
              <li><a href="mailto:info@justpeachybergen.no" className="hover:text-[#d4a088] transition-colors">info@justpeachybergen.no</a></li>
            </ul>
            <Link
              to={BOOKING_URL}
              className="inline-flex items-center gap-2 bg-[#cfa38e] text-white px-5 py-3 text-xs uppercase tracking-wider font-semibold mt-5 hover:bg-white hover:text-[#4a3f35] transition-all duration-400 rounded-full"
              data-testid="footer-book-btn"
            >
              {t.nav.book}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col items-center gap-4 text-center">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Just Peachy Bergen. {t.footer.rights}
          </p>
          <p className="text-white/50 text-sm flex items-center gap-1">
            {t.footer.madeWith} <Heart size={14} className="text-[#d4a088]" /> i Bergen
          </p>
          <p className="text-white/40 text-xs mt-2">
            Nettside laget av Kamilla Aabø med kjærlighet 🤍
          </p>
        </div>
      </div>
    </footer>
  );
};

// Sticky Mobile Booking
const StickyMobileBooking = () => {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-6 left-4 right-4 z-50 lg:hidden"
          data-testid="sticky-mobile-booking"
        >
          <Link
            to={BOOKING_URL}
            className="flex items-center justify-center gap-2 w-full bg-[#cfa38e] text-white py-4 rounded-full shadow-warm text-sm uppercase tracking-wider font-semibold"
          >
            {t.booking.sticky}
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Korean Lash Lift Educational Page
const LashLiftPage = () => {
  const { t } = useLanguage();
  const lash = t.lashLiftPage;

  return (
    <div className="pt-24" data-testid="lash-lift-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#FCE8E0] to-[#ffe2d0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#d4a088] mb-3 font-semibold">
              {lash.tagline}
            </p>
            <h1 className="font-serif text-4xl md:text-6xl text-[#4a3f35] leading-tight font-medium mb-4">
              {lash.title} <span className="text-[#d4a088] italic">{lash.titleHighlight}</span>
            </h1>
            <p className="text-lg text-[#6b5d52] max-w-3xl mx-auto">{lash.subtitle}</p>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid md:grid-cols-3 gap-4"
          >
            <div className="aspect-[4/3] rounded-2xl overflow-hidden img-zoom">
              <img src={SALON_IMAGES.lashTreatment} alt="Vippeløft behandling" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden img-zoom">
              <img src={SALON_IMAGES.kamillaWorking} alt="Kamilla gir vippeløft" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden img-zoom">
              <img src={SALON_IMAGES.happyClient} alt="Fornøyd kunde etter vippeløft" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-[#ffe2d0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-lg text-[#4a3f35] leading-relaxed"
          >
            {lash.intro}
          </motion.p>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 md:py-24 bg-[#ffe2d0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Traditional */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl p-8 shadow-soft relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-[#CDB4A8] text-white text-xs uppercase tracking-wider px-4 py-2 rounded-bl-xl">
                Tradisjonell
              </div>
              <h3 className="font-serif text-2xl text-[#4a3f35] mb-2 font-medium mt-4">{lash.regularTitle}</h3>
              <p className="text-sm text-[#d4a088] uppercase tracking-wider mb-4">{lash.regularSubtitle}</p>
              <p className="text-[#6b5d52] mb-6">{lash.regularDesc}</p>
              <ul className="space-y-3">
                {lash.regularPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-[#4a3f35]">
                    <span className="w-5 h-5 bg-[#FCE8E0] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-2 h-2 bg-[#CDB4A8] rounded-full"></span>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Korean */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-[#FCE8E0] to-[#FCE8E0] rounded-2xl p-8 shadow-warm relative overflow-hidden border-2 border-[#F8C8B4]"
            >
              <div className="absolute top-0 right-0 bg-[#cfa38e] text-white text-xs uppercase tracking-wider px-4 py-2 rounded-bl-xl font-semibold">
                Anbefalt
              </div>
              <h3 className="font-serif text-2xl text-[#4a3f35] mb-2 font-medium mt-4">{lash.koreanTitle}</h3>
              <p className="text-sm text-[#d4a088] uppercase tracking-wider mb-4">{lash.koreanSubtitle}</p>
              <p className="text-[#4a3f35] mb-6">{lash.koreanDesc}</p>
              <ul className="space-y-3">
                {lash.koreanPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-[#4a3f35]">
                    <Check size={18} className="text-[#d4a088] flex-shrink-0 mt-0.5" />
                    <span className="font-medium">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Korean Section */}
      <section className="py-16 md:py-24 bg-[#a68672]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4 font-medium">{lash.whyKorean}</h2>
            <p className="text-white/80 max-w-2xl mx-auto">{lash.whyKoreanDesc}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lash.whyPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 bg-[#cfa38e] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles size={24} className="text-white" />
                </div>
                <h4 className="font-serif text-lg text-white mb-2 font-medium">{point.title}</h4>
                <p className="text-white/70 text-sm">{point.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="py-16 md:py-24 bg-[#ffe2d0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-[#4a3f35] mb-4 font-medium">{lash.suitableTitle}</h2>
              <p className="text-[#6b5d52] mb-6">{lash.suitableDesc}</p>
              <ul className="space-y-3 mb-6">
                {lash.suitablePoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#4a3f35]">
                    <Check size={18} className="text-[#d4a088]" />
                    {point}
                  </li>
                ))}
              </ul>
              <div className="bg-[#FCE8E0] rounded-xl p-4 text-sm text-[#4a3f35]">
                <strong>OBS:</strong> {lash.notSuitable}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="aspect-square rounded-2xl overflow-hidden img-zoom"
            >
              <img src={SALON_IMAGES.happyClient} alt="Fornøyd kunde" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results & Duration */}
      <section className="py-16 md:py-24 bg-[#FCE8E0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-[#4a3f35] mb-4 font-medium">{lash.resultsTitle}</h2>
            <p className="text-[#6b5d52]">{lash.resultsDesc}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Results */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-soft"
            >
              <Star size={28} className="text-[#d4a088] mb-4" />
              <h4 className="font-serif text-xl text-[#4a3f35] mb-3">Resultater</h4>
              <ul className="space-y-2">
                {lash.resultsPoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-[#4a3f35]">
                    <Check size={14} className="text-[#d4a088]" />
                    {point}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Duration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-soft"
            >
              <Clock size={28} className="text-[#d4a088] mb-4" />
              <h4 className="font-serif text-xl text-[#4a3f35] mb-3">{lash.duration}</h4>
              <p className="text-[#4a3f35] font-medium text-lg mb-2">6-8 uker</p>
              <p className="text-sm text-[#6b5d52]">{lash.durationText}</p>
            </motion.div>

            {/* Treatment Time */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-soft"
            >
              <Coffee size={28} className="text-[#d4a088] mb-4" />
              <h4 className="font-serif text-xl text-[#4a3f35] mb-3">{lash.treatmentTime}</h4>
              <p className="text-[#4a3f35] font-medium text-lg mb-2">45-60 min</p>
              <p className="text-sm text-[#6b5d52]">{lash.treatmentTimeText}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Aftercare */}
      <section className="py-16 md:py-24 bg-[#ffe2d0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-warm"
          >
            <h2 className="font-serif text-2xl md:text-3xl text-[#4a3f35] mb-4 font-medium text-center">{lash.aftercareTitle}</h2>
            <p className="text-[#6b5d52] text-center mb-8">{lash.aftercareDesc}</p>
            <div className="grid md:grid-cols-2 gap-4">
              {lash.aftercarePoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#FCE8E0] rounded-xl p-4">
                  <div className="w-6 h-6 bg-[#cfa38e] rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    {index + 1}
                  </div>
                  <p className="text-sm text-[#4a3f35]">{point}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#FCE8E0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-[#4a3f35] font-medium">{lash.faqTitle}</h2>
          </motion.div>

          <div className="space-y-4">
            {lash.faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-soft"
              >
                <h4 className="font-medium text-[#4a3f35] mb-2 flex items-start gap-3">
                  <span className="text-[#d4a088]">Q:</span>
                  {faq.q}
                </h4>
                <p className="text-[#6b5d52] pl-6">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F8C8B4] to-[#D4956A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4 font-medium">{lash.ctaTitle}</h2>
            <p className="text-white/90 mb-2">{lash.ctaDesc}</p>
            <p className="text-2xl font-serif text-white mb-8">{lash.price}</p>
            <Link
              to={BOOKING_URL}
              className="inline-flex items-center gap-3 bg-white text-[#4a3f35] hover:bg-[#a68672] hover:text-white px-10 py-5 text-sm uppercase tracking-[0.15em] font-semibold transition-all duration-400 rounded-full shadow-warm"
              data-testid="lash-lift-cta"
            >
              {lash.ctaButton}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Page Components
// Waitlist Section for Homepage
const WaitlistSection = () => {
  const { t } = useContext(LanguageContext);
  const [showWaitlist, setShowWaitlist] = useState(false);

  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="waitlist-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#cfa38e]/10 via-[#FCE8E0] to-[#E6C7B8]/20"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-[#cfa38e]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#E6C7B8]/20 rounded-full blur-3xl"></div>
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Tagline */}
          <span className="inline-flex items-center gap-2 bg-[#cfa38e]/20 text-[#8a6d5c] px-5 py-2 rounded-full text-sm uppercase tracking-widest mb-6">
            <Clock size={16} />
            {t.waitlist.homepage.tagline}
          </span>
          
          {/* Title */}
          <h2 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl text-[#4a3f35] mb-6">
            {t.waitlist.homepage.title}{' '}
            <span className="text-[#cfa38e] italic">{t.waitlist.homepage.titleHighlight}</span>
          </h2>
          
          {/* Description */}
          <p className="text-[#6b5d52] text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            {t.waitlist.homepage.description}
          </p>
          
          {/* CTA Button */}
          <button
            onClick={() => setShowWaitlist(true)}
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#cfa38e] text-white rounded-full font-medium text-lg hover:bg-[#b8917d] transition-all shadow-xl shadow-[#cfa38e]/30 group"
            data-testid="waitlist-homepage-button"
          >
            <Clock size={22} />
            {t.waitlist.homepage.cta}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>

      {/* Waitlist Popup */}
      <WaitlistPopup isOpen={showWaitlist} onClose={() => setShowWaitlist(false)} />
    </section>
  );
};

const HomePage = () => (
  <>
    <HeroSection />
    <AboutSection />
    <ServicesSection />
    <WaitlistSection />
    <CoursesSection />
    <TestimonialsSection />
    <BeforeAfterSection />
    <VIPSection />
    <GiftCardSection />
    <InstagramSection />
    <ContactSection />
  </>
);

const ServicesPage = () => <ServicesSection fullPage />;
const AboutPage = () => <AboutSection fullPage />;

// Waitlist Popup Component
// Full treatment catalogue mirrors Timma. Grouped by category so the dropdown
// shows <optgroup> per category, and staff filtering derives from category.
// { no, en } labels live inline (only used in this form) to avoid huge translation blobs.
const WAITLIST_CATEGORIES = [
  {
    key: 'intimvoks',
    label: { no: 'Intimvoks', en: 'Intimate waxing' },
    staff: ['kamilla', 'thea'],
    items: [
      { key: 'iv-brazilian', no: 'Brasiliansk', en: 'Brazilian' },
      { key: 'iv-utvidet-bikini', no: 'Utvidet Bikini', en: 'Extended Bikini' },
      { key: 'iv-ordinar-bikini', no: 'Ordinær Bikini', en: 'Regular Bikini' },
      { key: 'iv-rumpe-ytre', no: 'Rumpe (ytre område)', en: 'Buttocks (outer area)' },
      { key: 'iv-rumpe-full', no: 'Rumpe (full behandling)', en: 'Buttocks (full)' },
      { key: 'iv-brazilian-legger', no: 'Brasiliansk + Legger', en: 'Brazilian + Calves' },
      { key: 'iv-brazilian-lar', no: 'Brasiliansk + Lår', en: 'Brazilian + Thighs' },
      { key: 'iv-brazilian-legger-lar', no: 'Brasiliansk + Legger + Lår', en: 'Brazilian + Calves + Thighs' },
      { key: 'iv-utvidet-bikini-legger', no: 'Utvidet Bikini + Legger', en: 'Extended Bikini + Calves' },
      { key: 'iv-utvidet-bikini-lar', no: 'Utvidet Bikini + Lår', en: 'Extended Bikini + Thighs' },
      { key: 'iv-utvidet-bikini-legger-lar', no: 'Utvidet Bikini + Legger + Lår', en: 'Extended Bikini + Calves + Thighs' },
      { key: 'iv-ordinar-bikini-legger', no: 'Ordinær Bikini + Legger', en: 'Regular Bikini + Calves' },
      { key: 'iv-ordinar-bikini-lar', no: 'Ordinær Bikini + Lår', en: 'Regular Bikini + Thighs' },
      { key: 'iv-ordinar-bikini-legger-lar', no: 'Ordinær Bikini + Legger + Lår', en: 'Regular Bikini + Calves + Thighs' },
    ],
  },
  {
    key: 'kroppsvoks',
    label: { no: 'Kroppsvoks', en: 'Body waxing' },
    staff: ['kamilla', 'thea', 'nora'],
    items: [
      { key: 'kv-legger', no: 'Legger', en: 'Calves' },
      { key: 'kv-lar', no: 'Lår', en: 'Thighs' },
      { key: 'kv-legger-lar', no: 'Legger + Lår', en: 'Calves + Thighs' },
      { key: 'kv-armhuler', no: 'Armhuler', en: 'Underarms' },
      { key: 'kv-armer', no: 'Armer', en: 'Arms' },
      { key: 'kv-mage', no: 'Mage', en: 'Stomach' },
      { key: 'kv-mage-navlestripe', no: 'Mage/Navlestripe', en: 'Stomach/Belly line' },
      { key: 'kv-bryst-mage', no: 'Bryst + Mage (kvinner)', en: 'Chest + Stomach (women)' },
      { key: 'kv-rygg', no: 'Rygg (kvinner)', en: 'Back (women)' },
      { key: 'kv-nedre-rygg', no: 'Nedre rygg', en: 'Lower back' },
      { key: 'kv-hele-kroppen', no: 'Hele kroppen', en: 'Full body' },
    ],
  },
  {
    key: 'ansiktsvoks',
    label: { no: 'Ansiktsvoks', en: 'Face waxing' },
    staff: ['kamilla', 'thea', 'lyra', 'nora'],
    items: [
      { key: 'av-hele-ansiktet-med-bryn', no: 'Hele ansiktet + forming av bryn', en: 'Full face + brow shaping' },
      { key: 'av-hele-ansiktet-uten-bryn', no: 'Hele ansiktet utenom bryn', en: 'Full face (no brows)' },
      { key: 'av-overleppe', no: 'Overleppe', en: 'Upper lip' },
      { key: 'av-hake', no: 'Hake', en: 'Chin' },
      { key: 'av-kinn', no: 'Kinn', en: 'Cheeks' },
      { key: 'av-orer', no: 'Ører', en: 'Ears' },
      { key: 'av-nakke', no: 'Nakke', en: 'Neck' },
      { key: 'av-nesebor', no: 'Nesebor', en: 'Nostrils' },
    ],
  },
  {
    key: 'koreansk-vippeloft',
    label: { no: 'Koreansk Vippeløft', en: 'Korean Lash Lift' },
    staff: ['kamilla', 'lyra', 'thea'],
    items: [
      { key: 'kvl-farge-oppe', no: 'Koreansk Vippeløft med farge kun oppe', en: 'Korean Lash Lift with tint (upper only)' },
      { key: 'kvl-farge-oppe-nede', no: 'Koreansk Vippeløft med farge oppe og nede', en: 'Korean Lash Lift with tint (upper and lower)' },
    ],
  },
  {
    key: 'lash-filler',
    label: { no: 'Lash Filler Vippeløft', en: 'Lash Filler Lift' },
    staff: ['kamilla', 'thea'],
    items: [
      { key: 'lf-farge-oppe', no: 'Lash Filler Vippeløft med farge kun oppe', en: 'Lash Filler Lift with tint (upper only)' },
      { key: 'lf-farge-oppe-nede', no: 'Lash Filler Vippeløft med farge oppe og nede', en: 'Lash Filler Lift with tint (upper and lower)' },
      { key: 'lf-menn', no: 'Vippeløft for menn', en: 'Lash lift for men' },
    ],
  },
  {
    key: 'brynsbehandlinger',
    label: { no: 'Brynsbehandlinger', en: 'Brow treatments' },
    staff: ['kamilla', 'thea', 'lyra', 'nora'],
    items: [
      { key: 'br-brow-bomber', no: 'Brow Bomber Brynslaminering', en: 'Brow Bomber Lamination' },
      { key: 'br-hd-bryn', no: 'HD Bryn', en: 'HD Brows' },
      { key: 'br-hybrid-bryn', no: 'Hybrid Bryn', en: 'Hybrid Brows' },
      { key: 'br-hybridfarge', no: 'Hybridfarge', en: 'Hybrid tint' },
      { key: 'br-forming', no: 'Forming av bryn', en: 'Brow shaping' },
      { key: 'br-brynsfarge', no: 'Brynsfarge', en: 'Brow tint' },
    ],
  },
  {
    key: 'gelenegler',
    label: { no: 'Gelenegler', en: 'Gel Nails' },
    staff: ['thea'],
    items: [
      { key: 'gn-nytt-uten-design', no: 'Nytt sett gele negler uten design', en: 'New gel nails, no design' },
      { key: 'gn-nytt-minimalt', no: 'Nytt sett gele negler minimalt design/fransk', en: 'New gel nails, minimal/French design' },
      { key: 'gn-nytt-avansert', no: 'Nytt sett gele negler avansert design', en: 'New gel nails, advanced design' },
      { key: 'gn-pafyll-uten-design', no: 'Påfyll gele negler uten design', en: 'Gel nail refill, no design' },
      { key: 'gn-pafyll-minimalt', no: 'Påfyll gele negler minimalt design/fransk', en: 'Gel nail refill, minimal/French' },
      { key: 'gn-pafyll-avansert', no: 'Påfyll gele negler mye/krevende design', en: 'Gel nail refill, heavy/advanced design' },
      { key: 'gn-fjerning', no: 'Fjerning av gelenegler', en: 'Gel nail removal' },
    ],
  },
  {
    key: 'voksing-menn',
    label: { no: 'Voksing av menn', en: 'Waxing for men' },
    staff: ['thea'],
    items: [
      { key: 'vm-manscaping', no: 'Manscaping', en: 'Manscaping' },
      { key: 'vm-rygg', no: 'Rygg', en: 'Back' },
      { key: 'vm-bryst-mage', no: 'Bryst + Mage', en: 'Chest + Stomach' },
      { key: 'vm-skuldre', no: 'Skuldre', en: 'Shoulders' },
      { key: 'vm-legger', no: 'Legger', en: 'Calves' },
      { key: 'vm-lar', no: 'Lår', en: 'Thighs' },
      { key: 'vm-legger-lar', no: 'Legger + Lår', en: 'Calves + Thighs' },
      { key: 'vm-armer', no: 'Armer', en: 'Arms' },
      { key: 'vm-hele-uten-manzilian', no: 'Hele kroppen uten manzilian', en: 'Full body (no manzilian)' },
      { key: 'vm-nesebor', no: 'Nesebor', en: 'Nostrils' },
      { key: 'vm-orer', no: 'Ører', en: 'Ears' },
    ],
  },
  {
    key: 'annet',
    label: { no: 'Annet', en: 'Other' },
    staff: ['kamilla', 'thea', 'lyra', 'nora'],
    items: [
      { key: 'annet', no: 'Annet', en: 'Other' },
    ],
  },
];

// Fast lookup: treatment key -> { categoryStaff, label:{no,en} }
const WAITLIST_TREATMENT_INDEX = WAITLIST_CATEGORIES.reduce((acc, cat) => {
  cat.items.forEach((item) => {
    acc[item.key] = { staff: cat.staff, no: item.no, en: item.en };
  });
  return acc;
}, {});

const WaitlistPopup = ({ isOpen, onClose }) => {
  const { t, lang } = useContext(LanguageContext);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    treatment: '',
    staff: '',
    preferredDays: [],
    preferredTimes: [],
    shortNotice: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Waitlist share helpers — links point to the waitlist landing on the site
  const waitlistUrl = 'https://justpeachybergen.no/#waitlist';
  const shareText = `${t.waitlist.success.shareMessage} ${waitlistUrl}`;

  const handleWhatsappShare = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
  };

  const handleSmsShare = () => {
    // sms: link with body — works on iOS and most Android SMS apps
    window.location.href = `sms:?&body=${encodeURIComponent(shareText)}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // When treatment changes, reset staff if current pick is not qualified for the new category
      if (name === 'treatment') {
        const allowed = (WAITLIST_TREATMENT_INDEX[value] && WAITLIST_TREATMENT_INDEX[value].staff) || [];
        if (prev.staff && prev.staff !== 'noPreference' && !allowed.includes(prev.staff)) {
          next.staff = '';
        }
      }
      return next;
    });
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Build url-encoded body for Netlify Forms.
    // Netlify expects a POST to "/" with application/x-www-form-urlencoded
    // and a "form-name" field matching the form's name attribute.
    const encode = (data) =>
      Object.keys(data)
        .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
        .join('&');

    const treatmentInfo = WAITLIST_TREATMENT_INDEX[formData.treatment];
    const treatmentLabel = treatmentInfo ? treatmentInfo[lang] || treatmentInfo.no : formData.treatment;

    const payload = {
      'form-name': 'waitlist',
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      treatment: treatmentLabel,
      staff: formData.staff,
      preferredDays: formData.preferredDays.join(', '),
      preferredTimes: formData.preferredTimes.join(', '),
      shortNotice: formData.shortNotice,
      notes: formData.notes,
    };

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(payload),
      });
      setIsSuccess(true);
    } catch (err) {
      console.error('Waitlist submission failed:', err);
      alert('Noe gikk galt. Vennligst prøv igjen eller kontakt oss direkte.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      treatment: '',
      staff: '',
      preferredDays: [],
      preferredTimes: [],
      shortNotice: '',
      notes: ''
    });
    setIsSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="bg-[#FFF8F5] rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#FFF8F5] px-6 py-5 border-b border-[#E6C7B8]/30 flex items-center justify-between z-10">
            <div>
              <h2 className="font-cormorant text-2xl text-[#4a3f35]">{t.waitlist.title}</h2>
              <p className="text-sm text-[#6b5d52] mt-1">{t.waitlist.subtitle}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-[#E6C7B8]/20 transition-colors"
              data-testid="waitlist-close"
            >
              <X size={20} className="text-[#6b5d52]" />
            </button>
          </div>

          {isSuccess ? (
            /* Success Message + Share */
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-20 h-20 bg-[#cfa38e] rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Check size={40} className="text-white" />
              </motion.div>
              <h3 className="font-cormorant text-3xl text-[#4a3f35] mb-3">{t.waitlist.success.title}</h3>
              <p className="text-[#6b5d52] mb-8">{t.waitlist.success.message}</p>

              {/* Share section */}
              <div className="border-t border-[#E6C7B8]/40 pt-6 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Share2 size={18} className="text-[#cfa38e]" />
                  <h4 className="font-cormorant text-xl text-[#4a3f35]">
                    {t.waitlist.success.shareTitle}
                  </h4>
                </div>
                <p className="text-sm text-[#6b5d52] mb-5">
                  {t.waitlist.success.shareSubtitle}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={handleWhatsappShare}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] text-white text-sm font-medium hover:bg-[#20b858] transition-colors shadow-md"
                    data-testid="waitlist-share-whatsapp"
                  >
                    <MessageCircle size={16} />
                    {t.waitlist.success.shareWhatsapp}
                  </button>
                  <button
                    type="button"
                    onClick={handleSmsShare}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#4a3f35] text-white text-sm font-medium hover:bg-[#3a3128] transition-colors shadow-md"
                    data-testid="waitlist-share-sms"
                  >
                    <MessageSquare size={16} />
                    {t.waitlist.success.shareSms}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-colors shadow-md ${
                      copied
                        ? 'bg-[#cfa38e] text-white'
                        : 'bg-white text-[#4a3f35] border border-[#E6C7B8] hover:bg-[#FFF8F5]'
                    }`}
                    data-testid="waitlist-share-copy"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? t.waitlist.success.shareCopied : t.waitlist.success.shareCopy}
                  </button>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="px-8 py-3 bg-white text-[#4a3f35] border border-[#E6C7B8] rounded-full font-medium hover:bg-[#FFF8F5] transition-colors"
                data-testid="waitlist-success-close"
              >
                {t.waitlist.success.close}
              </button>
            </div>
          ) : (
            /* Form */
            <form 
              onSubmit={handleSubmit} 
              name="waitlist" 
              method="POST" 
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="p-6 space-y-5"
            >
              <input type="hidden" name="form-name" value="waitlist" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-2">
                  {t.waitlist.form.name} *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.waitlist.form.namePlaceholder}
                  className="w-full px-4 py-3 rounded-xl border border-[#E6C7B8]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#cfa38e]/50 focus:border-[#cfa38e] transition-all text-[#4a3f35] placeholder:text-[#9a8b82]"
                />
              </div>

              {/* Phone & Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#4a3f35] mb-2">
                    {t.waitlist.form.phone} *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t.waitlist.form.phonePlaceholder}
                    className="w-full px-4 py-3 rounded-xl border border-[#E6C7B8]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#cfa38e]/50 focus:border-[#cfa38e] transition-all text-[#4a3f35] placeholder:text-[#9a8b82]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a3f35] mb-2">
                    {t.waitlist.form.email} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t.waitlist.form.emailPlaceholder}
                    className="w-full px-4 py-3 rounded-xl border border-[#E6C7B8]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#cfa38e]/50 focus:border-[#cfa38e] transition-all text-[#4a3f35] placeholder:text-[#9a8b82]"
                  />
                </div>
              </div>

              {/* Treatment */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-2">
                  {t.waitlist.form.treatment} *
                </label>
                <select
                  name="treatment"
                  required
                  value={formData.treatment}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#E6C7B8]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#cfa38e]/50 focus:border-[#cfa38e] transition-all text-[#4a3f35] appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b5d52'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5rem' }}
                >
                  <option value="">{t.waitlist.form.treatmentPlaceholder}</option>
                  {WAITLIST_CATEGORIES.map((cat) => (
                    <optgroup key={cat.key} label={cat.label[lang] || cat.label.no}>
                      {cat.items.map((item) => (
                        <option key={item.key} value={item.key}>
                          {item[lang] || item.no}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Staff Preference */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-2">
                  {t.waitlist.form.staff}
                </label>
                <select
                  name="staff"
                  value={formData.staff}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#E6C7B8]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#cfa38e]/50 focus:border-[#cfa38e] transition-all text-[#4a3f35] appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b5d52'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5rem' }}
                >
                  <option value="">{t.waitlist.form.staffPlaceholder}</option>
                  <option value="noPreference">{t.waitlist.form.staffOptions.noPreference}</option>
                  {(formData.treatment && WAITLIST_TREATMENT_INDEX[formData.treatment]
                    ? WAITLIST_TREATMENT_INDEX[formData.treatment].staff
                    : ['kamilla', 'thea', 'lyra', 'nora']
                  ).map((key) => (
                    <option key={key} value={key}>
                      {t.waitlist.form.staffOptions[key]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preferred Days */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-3">
                  {t.waitlist.form.preferredDays}
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(t.waitlist.form.days).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleCheckboxChange('preferredDays', key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        formData.preferredDays.includes(key)
                          ? 'bg-[#cfa38e] text-white shadow-md'
                          : 'bg-white text-[#4a3f35] border border-[#E6C7B8]/50 hover:border-[#cfa38e]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="preferredDays" value={formData.preferredDays.join(', ')} />
              </div>

              {/* Preferred Times */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-3">
                  {t.waitlist.form.preferredTimes}
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(t.waitlist.form.times).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleCheckboxChange('preferredTimes', key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        formData.preferredTimes.includes(key)
                          ? 'bg-[#cfa38e] text-white shadow-md'
                          : 'bg-white text-[#4a3f35] border border-[#E6C7B8]/50 hover:border-[#cfa38e]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="preferredTimes" value={formData.preferredTimes.join(', ')} />
              </div>

              {/* Short Notice */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-3">
                  {t.waitlist.form.shortNotice}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, shortNotice: 'yes' }))}
                    className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      formData.shortNotice === 'yes'
                        ? 'bg-[#cfa38e] text-white shadow-md'
                        : 'bg-white text-[#4a3f35] border border-[#E6C7B8]/50 hover:border-[#cfa38e]'
                    }`}
                  >
                    {t.waitlist.form.shortNoticeYes}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, shortNotice: 'no' }))}
                    className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      formData.shortNotice === 'no'
                        ? 'bg-[#cfa38e] text-white shadow-md'
                        : 'bg-white text-[#4a3f35] border border-[#E6C7B8]/50 hover:border-[#cfa38e]'
                    }`}
                  >
                    {t.waitlist.form.shortNoticeNo}
                  </button>
                </div>
                <input type="hidden" name="shortNotice" value={formData.shortNotice} />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-[#4a3f35] mb-2">
                  {t.waitlist.form.notes}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder={t.waitlist.form.notesPlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#E6C7B8]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#cfa38e]/50 focus:border-[#cfa38e] transition-all text-[#4a3f35] placeholder:text-[#9a8b82] resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#cfa38e] text-white rounded-full font-medium text-lg hover:bg-[#b8917d] transition-all shadow-lg shadow-[#cfa38e]/30 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="waitlist-submit"
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                    {t.waitlist.form.submitting}
                  </>
                ) : (
                  <>
                    <Clock size={20} />
                    {t.waitlist.form.submit}
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Booking Page Component
const BookingPage = () => {
  const { t } = useContext(LanguageContext);
  const [showWaitlist, setShowWaitlist] = useState(false);

  return (
    <div className="min-h-screen bg-[#ffe2d0]" data-testid="booking-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FCE8E0]/50 to-transparent"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-[#d4a088] text-sm uppercase tracking-widest mb-6">
              <Clock size={16} />
              {t.booking.tagline}
            </span>
            <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl text-[#4a3f35] mb-6">
              {t.booking.title} <span className="text-[#d4a088] italic">{t.booking.titleHighlight}</span>
            </h1>
            <p className="text-[#6b5d52] max-w-2xl mx-auto leading-relaxed">
              {t.booking.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Waitlist CTA Section */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-r from-[#FFF8F5] to-[#FCE8E0] rounded-2xl p-8 text-center border border-[#E6C7B8]/30"
        >
          <p className="text-[#6b5d52] mb-4">
            {t.waitlist.noTimesText}
          </p>
          <button
            onClick={() => setShowWaitlist(true)}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#cfa38e] text-white rounded-full font-medium hover:bg-[#b8917d] transition-all shadow-lg shadow-[#cfa38e]/30 group"
            data-testid="waitlist-button"
          >
            <Clock size={20} />
            {t.waitlist.button}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </section>

      {/* Booking iframe */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-3xl shadow-soft overflow-hidden"
        >
          <iframe 
            width="100%" 
            frameBorder="0"
            src="/book-embed.html"
            id="bookingWrapperFrame"
            title="Book behandling"
            style={{ minHeight: '780px', border: 0, display: 'block' }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-storage-access-by-user-activation"
          ></iframe>
        </motion.div>
      </section>

      {/* Sticky Waitlist Button (Mobile) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#ffe2d0] via-[#ffe2d0] to-transparent md:hidden z-40">
        <button
          onClick={() => setShowWaitlist(true)}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#cfa38e] text-white rounded-full font-medium hover:bg-[#b8917d] transition-all shadow-xl shadow-[#cfa38e]/40"
          data-testid="waitlist-sticky-button"
        >
          <Clock size={18} />
          <span>Venteliste</span>
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Waitlist Popup */}
      <WaitlistPopup isOpen={showWaitlist} onClose={() => setShowWaitlist(false)} />
    </div>
  );
};

// FAQ Accordion Item Component
const FAQAccordionItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-[#E6C7B8]/30 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-6 px-6 flex items-center justify-between text-left hover:bg-[#FCE8E0]/30 transition-all duration-300"
        data-testid={`faq-question-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="font-cormorant text-lg md:text-xl text-[#4a3f35] pr-4">{question}</span>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-[#cfa38e]/20 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#cfa38e] rotate-180' : ''}`}>
          {isOpen ? (
            <Minus size={16} className="text-white" />
          ) : (
            <Plus size={16} className="text-[#d4a088]" />
          )}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-[#6b5d52] leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// FAQ Category Component
const FAQCategory = ({ title, faqs, openIndex, setOpenIndex, categoryKey }) => {
  return (
    <div className="mb-12">
      <h3 className="font-cormorant text-2xl md:text-3xl text-[#4a3f35] mb-6 flex items-center gap-3">
        <span className="w-8 h-[2px] bg-[#cfa38e]"></span>
        {title}
      </h3>
      <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(248,200,180,0.15)] overflow-hidden">
        {faqs.map((faq, index) => {
          const uniqueKey = `${categoryKey}-${index}`;
          return (
            <FAQAccordionItem
              key={uniqueKey}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === uniqueKey}
              onClick={() => setOpenIndex(openIndex === uniqueKey ? null : uniqueKey)}
            />
          );
        })}
      </div>
    </div>
  );
};

// FAQ Page Component
const FAQPage = () => {
  const { t } = useContext(LanguageContext);
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { key: 'all', label: 'Alle' },
    { key: 'waxing', label: t.faq.categories.waxing },
    { key: 'lashBrow', label: t.faq.categories.lashBrow },
    { key: 'booking', label: t.faq.categories.booking },
    { key: 'koreanLash', label: t.faq.categories.koreanLash },
    { key: 'brows', label: t.faq.categories.brows },
    { key: 'beforeTreatment', label: t.faq.categories.beforeTreatment },
    { key: 'afterTreatment', label: t.faq.categories.afterTreatment },
    { key: 'intimateWax', label: t.faq.categories.intimateWax },
    { key: 'painComfort', label: t.faq.categories.painComfort },
    { key: 'comfort', label: t.faq.categories.comfort },
    { key: 'hygiene', label: t.faq.categories.hygiene },
    { key: 'pregnancy', label: t.faq.categories.pregnancy },
    { key: 'firstTime', label: t.faq.categories.firstTime },
    { key: 'courses', label: t.faq.categories.courses },
    { key: 'trainees', label: t.faq.categories.trainees },
    { key: 'reviews', label: t.faq.categories.reviews },
    { key: 'jobs', label: t.faq.categories.jobs },
    { key: 'student', label: t.faq.categories.student },
    { key: 'byVenn', label: t.faq.categories.byVenn },
    { key: 'parking', label: t.faq.categories.parking },
    { key: 'accessibility', label: t.faq.categories.accessibility },
    { key: 'allergies', label: t.faq.categories.allergies },
    { key: 'gelNails', label: t.faq.categories.gelNails },
    { key: 'general', label: t.faq.categories.general },
  ];

  return (
    <div className="min-h-screen bg-[#ffe2d0]" data-testid="faq-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FCE8E0]/50 to-transparent"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-[#d4a088] text-sm uppercase tracking-widest mb-6">
              <HelpCircle size={16} />
              {t.faq.tagline}
            </span>
            <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl text-[#4a3f35] mb-6">
              {t.faq.title} <span className="text-[#d4a088] italic">{t.faq.titleHighlight}</span>
            </h1>
            <p className="text-[#6b5d52] max-w-2xl mx-auto leading-relaxed">
              {t.faq.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3"
        >
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === cat.key
                  ? 'bg-[#cfa38e] text-white shadow-lg shadow-[#F8C8B4]/30'
                  : 'bg-white text-[#4a3f35] hover:bg-[#FCE8E0] border border-[#E6C7B8]/30'
              }`}
              data-testid={`faq-category-${cat.key}`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>
      </section>

      {/* FAQ Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {(activeCategory === 'all' || activeCategory === 'waxing') && (
            <FAQCategory
              title={t.faq.categories.waxing}
              faqs={t.faq.waxing}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="waxing"
            />
          )}
          
          {(activeCategory === 'all' || activeCategory === 'lashBrow') && (
            <FAQCategory
              title={t.faq.categories.lashBrow}
              faqs={t.faq.lashBrow}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="lashBrow"
            />
          )}
          
          {(activeCategory === 'all' || activeCategory === 'booking') && (
            <FAQCategory
              title={t.faq.categories.booking}
              faqs={t.faq.booking}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="booking"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'koreanLash') && t.faq.koreanLash && (
            <FAQCategory
              title={t.faq.categories.koreanLash}
              faqs={t.faq.koreanLash}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="koreanLash"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'brows') && t.faq.brows && (
            <FAQCategory
              title={t.faq.categories.brows}
              faqs={t.faq.brows}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="brows"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'beforeTreatment') && t.faq.beforeTreatment && (
            <FAQCategory
              title={t.faq.categories.beforeTreatment}
              faqs={t.faq.beforeTreatment}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="beforeTreatment"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'afterTreatment') && t.faq.afterTreatment && (
            <FAQCategory
              title={t.faq.categories.afterTreatment}
              faqs={t.faq.afterTreatment}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="afterTreatment"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'intimateWax') && t.faq.intimateWax && (
            <FAQCategory
              title={t.faq.categories.intimateWax}
              faqs={t.faq.intimateWax}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="intimateWax"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'painComfort') && t.faq.painComfort && (
            <FAQCategory
              title={t.faq.categories.painComfort}
              faqs={t.faq.painComfort}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="painComfort"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'comfort') && t.faq.comfort && (
            <FAQCategory
              title={t.faq.categories.comfort}
              faqs={t.faq.comfort}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="comfort"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'hygiene') && t.faq.hygiene && (
            <FAQCategory
              title={t.faq.categories.hygiene}
              faqs={t.faq.hygiene}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="hygiene"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'pregnancy') && t.faq.pregnancy && (
            <FAQCategory
              title={t.faq.categories.pregnancy}
              faqs={t.faq.pregnancy}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="pregnancy"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'firstTime') && t.faq.firstTime && (
            <FAQCategory
              title={t.faq.categories.firstTime}
              faqs={t.faq.firstTime}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="firstTime"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'courses') && t.faq.courses && (
            <FAQCategory
              title={t.faq.categories.courses}
              faqs={t.faq.courses}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="courses"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'trainees') && t.faq.trainees && (
            <FAQCategory
              title={t.faq.categories.trainees}
              faqs={t.faq.trainees}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="trainees"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'reviews') && t.faq.reviews && (
            <FAQCategory
              title={t.faq.categories.reviews}
              faqs={t.faq.reviews}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="reviews"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'jobs') && t.faq.jobs && (
            <FAQCategory
              title={t.faq.categories.jobs}
              faqs={t.faq.jobs}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="jobs"
            />
          )}
          
          {(activeCategory === 'all' || activeCategory === 'student') && (
            <FAQCategory
              title={t.faq.categories.student}
              faqs={t.faq.student}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="student"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'byVenn') && t.faq.byVenn && (
            <FAQCategory
              title={t.faq.categories.byVenn}
              faqs={t.faq.byVenn}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="byVenn"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'parking') && t.faq.parking && (
            <FAQCategory
              title={t.faq.categories.parking}
              faqs={t.faq.parking}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="parking"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'accessibility') && t.faq.accessibility && (
            <FAQCategory
              title={t.faq.categories.accessibility}
              faqs={t.faq.accessibility}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="accessibility"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'allergies') && t.faq.allergies && (
            <FAQCategory
              title={t.faq.categories.allergies}
              faqs={t.faq.allergies}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="allergies"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'gelNails') && t.faq.gelNails && (
            <FAQCategory
              title={t.faq.categories.gelNails}
              faqs={t.faq.gelNails}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="gelNails"
            />
          )}

          {(activeCategory === 'all' || activeCategory === 'general') && t.faq.general && (
            <FAQCategory
              title={t.faq.categories.general}
              faqs={t.faq.general}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              categoryKey="general"
            />
          )}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center bg-gradient-to-br from-[#F8C8B4]/20 to-[#FCE8E0]/30 rounded-3xl p-10 md:p-14"
        >
          <MessageCircle size={40} className="text-[#d4a088] mx-auto mb-6" />
          <h3 className="font-cormorant text-2xl md:text-3xl text-[#4a3f35] mb-4">
            {t.faq.ctaTitle}
          </h3>
          <p className="text-[#6b5d52] mb-8 max-w-md mx-auto">
            {t.faq.ctaDesc}
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-[#cfa38e] text-white px-8 py-4 rounded-full hover:bg-[#EFB39A] transition-all duration-300 shadow-lg shadow-[#F8C8B4]/30 hover:shadow-xl"
            data-testid="faq-contact-cta"
          >
            {t.faq.ctaButton}
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

const CoursesPage = () => <CoursesSection fullPage />;
const GiftCardPage = () => <GiftCardSection fullPage />;
const ContactPage = () => <ContactSection fullPage />;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// App Component
function App() {
  const [lang, setLang] = useState('no');
  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="App bg-[#ffe2d0]">
        <BrowserRouter>
          <ScrollToTop />
          <Navigation />
          <NewsletterPopup />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/behandlinger" element={<ServicesPage />} />
            <Route path="/om-oss" element={<AboutPage />} />
            <Route path="/kurs" element={<CoursesPage />} />
            <Route path="/gavekort" element={<GiftCardPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/vippeloft" element={<LashLiftPage />} />
            <Route path="/koreansk-vippeloft" element={<LashLiftPage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
          <Footer />
          <StickyMobileBooking />
        </BrowserRouter>
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
